import SceneConversation from 'components/Panel/Chat/Conversation/SceneConversation';
import FrameGallery from 'components/FrameGallery';
import Icon, { IconType } from 'components/Icon';
import ShareModal from 'components/Modal/ShareModal';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveScene, selectSceneChatRefs } from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import {
	selectSceneChatDiscardingDraft,
	setActiveSceneChatId,
	setCenteredFrameId,
	setSidebarVisible,
} from 'redux/mode/actions';
import { displayViewOnlyModal, startSceneChatDraft } from 'redux/mode/util';
import { RootReducer, selectActiveSceneChatOrDraft } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { logout } from 'utils';
import * as S from './styles';

const WorkspaceMobile = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [showShareModal, setShowShareModal] = useState(false);

	const {
		activeSceneChatId,
		activeScene,
		chatRefs,
		chatRefsLoaded,
		activeChat,
		centeredFrameId,
		isDraft,
	} = useSelector((r: RootReducer) => {
		return {
			activeSceneChatId: r.mode.activeSceneChatId,
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
			chatRefs: selectSceneChatRefs(r.db),
			chatRefsLoaded: r.db.loading.activeScene.chatRefs === 'loaded',
			activeChat: selectActiveSceneChatOrDraft(r),
			centeredFrameId: r.mode.centeredFrameId,
			// A brand-new chat is a client-only draft, never present in chatRefs.
			isDraft:
				r.mode.activeSceneChatId !== null &&
				r.mode.draftSceneChat?.id === r.mode.activeSceneChatId,
		};
	}, shallowEqual);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onNewChat = useCallback(() => {
		if (!activeScene) return;
		if (!canEdit(activeScene)) {
			displayViewOnlyModal();
			return;
		}

		// The new chat is a client-only draft; it hits the server only once its first
		// message is sent.
		startSceneChatDraft();
	}, [activeScene]);

	// Chat refs are already sorted by updatedAt DESC from the backend
	const sortedChatRefs = useMemo(() => {
		return chatRefs;
	}, [chatRefs]);

	// Falls back to the newest chat when the active one resolves to nothing. Holding a
	// chat is the signal to leave it alone: a draft or a freshly created chat (optimistic
	// insert, not yet in chatRefs) is legitimately missing from refs, and reselecting
	// would yank the user off the conversation they just started.
	// Only select once this scene's chat list has loaded — an unloaded list can't
	// say which chat is newest.
	useEffect(() => {
		if (activeChat) return;
		if (!chatRefsLoaded) return;

		if (!activeSceneChatId || !sortedChatRefs.some((ref) => ref.id === activeSceneChatId)) {
			const firstChatRef = sortedChatRefs.length > 0 ? sortedChatRefs[0] : null;
			if (firstChatRef) {
				dispatch(setActiveSceneChatId(firstChatRef.id));
			}
		}
	}, [sortedChatRefs, chatRefsLoaded, activeSceneChatId, dispatch, activeChat]);

	// Create menu items for chats dropdown
	const chatMenuItems = useMemo(() => {
		const items = [
			new MenuItemAction('Create new chat...', '', () => {
				onNewChat();
			}),
			new MenuItemDivider(),
		];

		if (sortedChatRefs.length > 0) {
			items.push(
				...sortedChatRefs.map((ref) => {
					const isActive = ref.id === activeSceneChatId;
					return new MenuItemAction(
						ref.title || 'Untitled Chat',
						'',
						() => {
							// Mobile has no tab strip, so an unsent draft left behind here would
							// be unreachable; selecting another chat drops it.
							dispatch(selectSceneChatDiscardingDraft(ref.id));
							dispatch(setSidebarVisible(false)); // Close sidebar when chat is selected
						},
						false,
						isActive // showCheckmark
					);
				})
			);
		}

		return items;
	}, [sortedChatRefs, activeSceneChatId, dispatch, onNewChat]);

	const moreMenuItems = [
		MenuItemAction.createReturnToProjectList(),
		new MenuItemAction('Log out', '', () => {
			logout();
		}),
		...(activeSceneChatId ? [MenuItemAction.createCopyChatId(activeSceneChatId)] : []),
		// A draft has no persisted row, so renaming it would fail — only offer rename
		// once the chat is real.
		...(activeSceneChatId && canEdit(activeScene) && !isDraft
			? [MenuItemAction.createRenameChat(activeSceneChatId)]
			: []),
	];

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	return (
		<S.Base>
			<S.Application>
				<S.Header>
					<S.HeaderLeft>
						<SidebarToggle />
						{activeChat && (
							<div>
								<DropdownMenu menuItems={chatMenuItems}>
									<S.ChatTitleButton>
										<S.ChatTitle>{activeChat.title || 'Untitled Chat'}</S.ChatTitle>
										<S.ChatTitleIcon>
											<Icon
												type={IconType.FILLED_ARROW_DOWN}
												width={16}
												height={16}
												color={theme.gray[800]}
											/>
										</S.ChatTitleIcon>
									</S.ChatTitleButton>
								</DropdownMenu>
							</div>
						)}
					</S.HeaderLeft>
					<S.HeaderRight>
						<S.HeaderButton onClick={() => setShowShareModal(true)}>
							<Icon type={IconType.SHARE} width={24} height={24} color={theme.gray[1000]} />
						</S.HeaderButton>
						<DropdownMenu menuItems={moreMenuItems}>
							<S.MoreButton>
								<S.InnerWrapper>
									<S.MoreButtonLogo>
										<Icon type={IconType.MORE2} width={24} height={24} color={theme.gray[1000]} />
									</S.MoreButtonLogo>
								</S.InnerWrapper>
							</S.MoreButton>
						</DropdownMenu>
					</S.HeaderRight>
				</S.Header>

				<S.MainContent>
					{activeSceneChatId ? (
						<SceneConversation />
					) : (
						<S.Placeholder>
							<S.PlaceholderIcon>
								<Icon type={IconType.CHAT} width={48} height={48} color={theme.gray[400]} />
							</S.PlaceholderIcon>
							<S.PlaceholderTitle>Start a new conversation</S.PlaceholderTitle>
							<S.PlaceholderDescription>
								Tap "New Chat" to begin designing with AI
							</S.PlaceholderDescription>
						</S.Placeholder>
					)}
				</S.MainContent>
			</S.Application>

			{centeredFrameId && (
				<FrameGallery
					frameId={centeredFrameId}
					sceneControls={true}
					onClose={() => dispatch(setCenteredFrameId(null))}
				/>
			)}
			{showShareModal && activeScene && (
				<ShareModal
					kind="scene"
					entityId={activeScene.id}
					onClose={() => setShowShareModal(false)}
				/>
			)}
		</S.Base>
	);
};

export default WorkspaceMobile;
